import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getModules } from './modules.service';
import { getPackageModules, addPackageModule, deletePackageModule } from './packages.service';
import { moduleQueryKeys, packageModuleQueryKeys } from './modules.query-keys';
import { useAppToast } from '@/hooks/useAppToast';

/**
 * Hook to fetch all modules in the system
 */
export const useAllModules = () => {
  return useQuery({
    queryKey: moduleQueryKeys.lists(),
    queryFn: () => getModules(),
  });
};

/**
 * Hook to fetch modules for a specific package
 */
export const usePackageModules = (packageId: string) => {
  return useQuery({
    queryKey: packageModuleQueryKeys.list(packageId),
    queryFn: () => getPackageModules(packageId),
    enabled: !!packageId,
  });
};

/**
 * Hook to add a module to a package - waits for backend response
 */
export const useAddPackageModule = () => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packageId, moduleId }: { packageId: string; moduleId: string }) =>
      addPackageModule(packageId, moduleId),
    onError: (err: any) => {
      console.error("Error adding module:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Erro ao adicionar módulo";
      toast.error(errorMessage);
    },
    onSuccess: () => {
      toast.success("Módulo adicionado com sucesso");
    },
    onSettled: (data, error, variables) => {
      // Invalidate to refetch after backend response
      queryClient.invalidateQueries({ 
        queryKey: packageModuleQueryKeys.list(variables.packageId) 
      });
    },
  });
};

/**
 * Hook to remove a module from a package - waits for backend response
 */
export const useRemovePackageModule = () => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packageId, moduleId }: { packageId: string; moduleId: string }) =>
      deletePackageModule(packageId, moduleId),
    onError: (err: any) => {
      console.error("Error removing module:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Erro ao remover módulo";
      toast.error(errorMessage);
    },
    onSuccess: () => {
      toast.success("Módulo removido com sucesso");
    },
    onSettled: (data, error, variables) => {
      // Invalidate to refetch after backend response
      queryClient.invalidateQueries({ 
        queryKey: packageModuleQueryKeys.list(variables.packageId) 
      });
    },
  });
};
